import { useState } from "react";
import { InboxIcon, PlusIcon } from "@heroicons/react/20/solid";
import { useDataset } from "../../../contexts/DatasetContext";
import { formatSecondsToFullDate } from "../../../helpers/date/date-helper";
import { ActionButton } from "../../../components/buttons/ActionButton";
import { Loader } from "../../../components/loading/Loader";
import { Modal } from "../../../components/modals/Modal";
import { Panel } from "../../../components/panels/Panel";
import Badge from "../../../components/badges/Badge";

export const AnnotationPageDatasets = () => {
  const {
    datasets,
    selectedDatasetId,
    isLoadingDatasets,
    isCreatingDataset,
    selectDataset,
    createDataset,
  } = useDataset();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [datasetName, setDatasetName] = useState('');

  const sortedDatasets = [...datasets].sort((datasetA, datasetB) => datasetB.creation_date - datasetA.creation_date);

  const getDatasetStateLabel = (state) => {
    if (state === 'waiting_capture') return 'Capture en cours';
    if (state === 'in_progress') return 'En cours';
    if (state === 'completed') return 'Terminé';
    return state;
  };

  const getDatasetStateBadgeColor = (state) => {
    if (state === 'waiting_capture') return 'perception';
    if (state === 'in_progress') return 'yellow';
    if (state === 'completed') return 'green';

    return 'secondary';
  };

  const getDatasetProgress = (dataset) => {
    if (dataset.state === 'waiting_capture')
      return `${dataset.total_images} image${dataset.total_images > 1 ? 's' : ''}`;

    return `${dataset.viewed_images}/${dataset.total_images} images traitées`;
  };

  const closeCreateModal = () => {
    setDatasetName('');
    setIsCreateModalOpen(false);
  };

  const handleCreateDataset = async () => {
    try {
      await createDataset(datasetName);
      closeCreateModal();
    } catch (error) {
      // Error is already handled by DatasetContext.
    }
  };

  return (
    <>
      <Modal isOpen={isCreateModalOpen} setIsOpen={setIsCreateModalOpen} staticBackdrop={isCreatingDataset} size="medium">
        <h3 className="mb-4 text-lg font-medium text-perception-gray-800">Créer un dossier</h3>

        <div className="flex flex-col gap-6">
          <input
            type="text"
            value={datasetName}
            onChange={(event) => setDatasetName(event.target.value)}
            placeholder="Nom du dossier"
            className="w-full rounded-md bg-perception-gray-100 px-3 py-2 text-perception-gray-800 outline-none focus:ring-1 focus:ring-perception-blue"
            disabled={isCreatingDataset}
          />

          <div className="flex justify-end gap-2">
            <ActionButton text="Annuler" color="cancel" disabled={isCreatingDataset} onClick={closeCreateModal} />
            <ActionButton text={isCreatingDataset ? 'Création...' : 'Créer'} disabled={isCreatingDataset} onClick={handleCreateDataset} />
          </div>
        </div>
      </Modal>

      <div className="flex flex-col h-full min-h-0 gap-4">
        <ActionButton text="Créer un dossier" Icon={PlusIcon} className="rounded-md justify-center" onClick={() => setIsCreateModalOpen(true)} />

        <div className="relative flex-1 min-h-0">
          {isLoadingDatasets ? (
            <Loader category="transparent" />
          ) : sortedDatasets.length === 0 ? (
            <div className="flex flex-col gap-4 h-full items-center justify-center text-center">
              <InboxIcon className="size-24 text-perception-gray-700" />
              <div className="text-perception-gray-700 text-xl">Aucun dossier</div>
            </div>
          ) : (
            <div className="h-full overflow-y-auto flex flex-col gap-4">
              {sortedDatasets.map((dataset) => {
                const isSelected = selectedDatasetId === dataset.id;

                return (
                  <button
                    key={dataset.id}
                    type="button"
                    className={`flex flex-col gap-2 bg-perception-black-700 p-4 rounded-md border-2 transition ${
                      isSelected ? 'border-perception-blue' : 'border-transparent hover:border-perception-gray-700'
                    }`}
                    onClick={() => selectDataset(dataset.id)}
                  >
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-white font-semibold truncate">{dataset.name}</span>
                      <span className="text-xs text-perception-gray-500 whitespace-nowrap">{formatSecondsToFullDate(dataset.creation_date)}</span>
                    </div>

                    <div className="flex gap-2">
                      <Badge text={getDatasetStateLabel(dataset.state)} type={getDatasetStateBadgeColor(dataset.state)} />
                      <Badge text={getDatasetProgress(dataset)} type="gray" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};